import { isPlatformBrowser } from '@angular/common';
import { Component, inject, PLATFORM_ID, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AcquisitionCodeService } from './core/services/acquisition-code.service';
import { AuthService } from './core/services/auth.service';
import { ChannelEngagementService } from './core/services/channel-engagement.service';
import { StorefrontTrafficService } from './core/services/storefront-traffic.service';
import { BagDrawerComponent } from './features/bag/bag-drawer.component';
import { FooterComponent } from './layout/footer/footer.component';
import { HeaderComponent } from './layout/header/header.component';
import { CartService } from './features/cart/cart.service';
import { WishlistService } from './features/wishlist/wishlist.service';
import { PageLoaderComponent } from './shared/components/page-loader/page-loader.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, FooterComponent, BagDrawerComponent, PageLoaderComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly title = signal('pryd-grains-app');

  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly acquisitionCodes = inject(AcquisitionCodeService);
  private readonly authService = inject(AuthService);
  private readonly channelEngagement = inject(ChannelEngagementService);
  private readonly storefrontTraffic = inject(StorefrontTrafficService);
  readonly cartService = inject(CartService);
  private readonly wishlistService = inject(WishlistService);
  private lastTrackedPath = '';
  private lastTrackedAt = 0;
  private trafficBootstrapped = false;

  /** Hide main mega-header on routes that render their own page-specific header. */
  protected readonly hideSiteHeader = signal(this.isMinimalHeaderRoute(this.normalizedPath(this.router.url)));

  /** Hide site footer on routes that render their own page-specific footer. */
  protected readonly hideSiteFooter = signal(this.isCheckoutOnlyRoute(this.normalizedPath(this.router.url)));

  constructor() {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((event) => {
      const path = this.normalizedPath(this.router.url);
      this.hideSiteHeader.set(this.isMinimalHeaderRoute(path));
      this.hideSiteFooter.set(this.isCheckoutOnlyRoute(path));
      this.syncBodyTemplateClass(path);
      this.acquisitionCodes.captureFromQueryParams(this.router.parseUrl(event.urlAfterRedirects).queryParams);
      this.trackChannelEngagement(this.normalizedPath(event.urlAfterRedirects));
      if (this.trafficBootstrapped) {
        this.storefrontTraffic.trackNavigation(event.urlAfterRedirects);
      }
      if (path === '/checkout' || path === '/login' || path === '/signup') {
        this.cartService.clearCheckoutNavigation();
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      this.syncBodyTemplateClass(this.normalizedPath(this.router.url));
      this.lastTrackedAt = Date.now();
      this.lastTrackedPath = this.normalizedPath(this.router.url);
      this.wishlistService.bootstrap();
      this.storefrontTraffic.bootstrap();
      this.trafficBootstrapped = true;
    }
  }

  private syncBodyTemplateClass(path: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.body.classList.toggle('template-index', path === '/' || path === '');
    document.body.classList.toggle('template-product', path.startsWith('/products/'));
    document.body.classList.toggle(
      'template-page--where-to-buy',
      path === '/where-to-buy' || path.startsWith('/where-to-buy/'),
    );
    document.body.classList.toggle('page-auth', this.isAuthShellRoute(path));
  }

  private normalizedPath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  private isMinimalHeaderRoute(path: string): boolean {
    return this.isAuthShellRoute(path);
  }

  private isCheckoutOnlyRoute(path: string): boolean {
    return this.isAuthShellRoute(path);
  }

  private isAuthShellRoute(path: string): boolean {
    return (
      path === '/login' ||
      path.startsWith('/login/') ||
      path === '/signup' ||
      path.startsWith('/signup/') ||
      path === '/forgot-password' ||
      path.startsWith('/forgot-password/')
    );
  }

  private trackChannelEngagement(nextPath: string): void {
    if (!isPlatformBrowser(this.platformId) || !this.authService.isAuthenticated()) {
      this.lastTrackedPath = nextPath;
      this.lastTrackedAt = Date.now();
      return;
    }

    const now = Date.now();
    const elapsedSeconds = this.lastTrackedAt > 0 ? Math.floor((now - this.lastTrackedAt) / 1000) : 0;
    const pagePath = this.lastTrackedPath || nextPath;

    this.channelEngagement
      .recordPageView({
        pagePath,
        durationSeconds: Math.min(Math.max(elapsedSeconds, 0), 3600),
      })
      .subscribe({ error: () => undefined });

    this.lastTrackedPath = nextPath;
    this.lastTrackedAt = now;
  }
}
